/**
 * Shared loan portfolio metric calculations.
 * Single source of truth — import from here; never inline these formulas.
 */

/** Convert any amount to ILS using fxRates map e.g. { USD: 3.62, EUR: 4.0 } */
export function toILS(amount, currency, fxRates) {
  if (!amount) return 0;
  if (!currency || currency === "ILS") return Number(amount);
  const rate = fxRates?.[currency];
  return rate ? Number(amount) * rate : Number(amount);
}

/**
 * Total outstanding debt in ILS across a set of loans.
 * Uses remainingPrincipal (or principal if not set), converted to ILS via fxRates.
 */
export function computeTotalDebtILS(loans, fxRates = {}) {
  return loans.reduce((sum, l) => {
    const remaining = Number(l.remainingPrincipal ?? l.principal) || 0;
    return sum + toILS(remaining, l.currency, fxRates);
  }, 0);
}

/**
 * Weighted-average interest rate across a set of loans.
 * Weights by remainingPrincipal (converted to ILS), includes all rates (negative, zero, positive).
 * Returns null if there are no loans with remaining principal.
 */
export function computeWeightedAvgRate(loans, fxRates = {}) {
  let sw = 0;
  let swRate = 0;
  for (const l of loans) {
    const rate = Number(l.interestRate);
    const remaining = Number(l.remainingPrincipal ?? l.principal) || 0;
    const p = toILS(remaining, l.currency, fxRates);
    if (!Number.isNaN(rate) && p > 0) {
      sw += p;
      swRate += rate * p;
    }
  }
  return sw > 0 ? swRate / sw : null;
}
